import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SubLocationManager } from "@/components/SubLocationManager";

const EMPTY = { name: "", description: "", active: true, requires_sublocation: false };

export default function AdminSites() {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [subSite, setSubSite] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/sites")
            .then(({ data }) => { if (!cancelled) setSites(data); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [refreshTick]);

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (site) => {
        setEditing(site);
        setForm({
            name: site.name,
            description: site.description || "",
            active: site.active,
            requires_sublocation: !!site.requires_sublocation,
        });
        setOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Site name is required");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/sites/${editing.id}`, form);
                toast.success("Site updated");
            } else {
                await api.post("/sites", form);
                toast.success("Site created");
            }
            setOpen(false);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (site) => {
        try {
            await api.patch(`/sites/${site.id}`, { active: !site.active });
            toast.success(`Site ${!site.active ? "activated" : "deactivated"}`);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const handleDelete = async (site) => {
        if (!window.confirm(`Delete site "${site.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/sites/${site.id}`);
            toast.success("Site deleted");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Sites
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Sites Management
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm max-w-2xl">
                        Manage the work-sites that appear in payment report dropdowns and CSV imports.
                        Deactivate sites you've retired — they'll stay hidden from new entries but
                        existing reports keep their history intact.
                    </p>
                </div>
                <Button
                    onClick={openCreate}
                    className="bg-[#0066D9] hover:bg-[#0052ad] text-white h-10"
                    data-testid="create-site-button"
                >
                    <Plus size={16} className="mr-1.5" /> New site
                </Button>
            </div>

            <Card className="border-[#E2E8F0]" data-testid="sites-card">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-10 text-center text-slate-500">Loading sites…</div>
                    ) : sites.length === 0 ? (
                        <div className="p-10 sm:p-16 text-center" data-testid="sites-empty-state">
                            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <Buildings size={26} weight="regular" className="text-slate-400" />
                            </div>
                            <h3 className="font-semibold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                No sites yet
                            </h3>
                            <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">
                                Add your first work-site (e.g. <span className="font-mono">IAD-39</span>) so technicians
                                and CSV imports can select it from a clean dropdown.
                            </p>
                            <Button
                                onClick={openCreate}
                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white mt-5"
                                data-testid="create-first-site-button"
                            >
                                <Plus size={16} className="mr-1.5" /> Add your first site
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Site name</TableHead>
                                        <TableHead className="hidden sm:table-cell">Description</TableHead>
                                        <TableHead>Sub-location</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sites.map((s) => (
                                        <TableRow key={s.id} data-testid={`site-row-${s.id}`}>
                                            <TableCell className="font-semibold text-[#003366]">{s.name}</TableCell>
                                            <TableCell className="hidden sm:table-cell text-slate-600 text-sm">
                                                {s.description || <span className="text-slate-400">—</span>}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {s.requires_sublocation ? (
                                                        <Badge className="bg-[#0066D9] text-white text-[10px] uppercase tracking-wider">
                                                            Required
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">Not required</span>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setSubSite(s)}
                                                        className="h-7 px-2 text-[#0066D9] text-xs"
                                                        data-testid={`manage-sublocations-${s.id}`}
                                                    >
                                                        <Stack size={14} className="mr-1" /> Manage
                                                    </Button>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {s.active ? (
                                                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] uppercase tracking-wider font-semibold">
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-slate-200 text-slate-700 border border-slate-300 text-[10px] uppercase tracking-wider font-semibold">
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => toggleActive(s)}
                                                        className="h-8 px-2 text-slate-600"
                                                        data-testid={`toggle-site-${s.id}`}
                                                        title={s.active ? "Deactivate" : "Activate"}
                                                    >
                                                        {s.active
                                                            ? <ToggleRight size={20} weight="fill" className="text-emerald-600" />
                                                            : <ToggleLeft size={20} className="text-slate-400" />}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => openEdit(s)}
                                                        className="h-8 px-2"
                                                        data-testid={`edit-site-${s.id}`}
                                                    >
                                                        <PencilSimple size={14} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleDelete(s)}
                                                        className="h-8 px-2 text-[#E6002C] hover:text-[#E6002C] hover:bg-red-50"
                                                        data-testid={`delete-site-${s.id}`}
                                                    >
                                                        <Trash size={14} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-md" data-testid="site-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            {editing ? "Edit site" : "New site"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Site name <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. IAD-39"
                                className="h-11"
                                data-testid="site-name-input"
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Description
                            </Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder="Optional — what is this site, who's the customer, etc."
                                className="min-h-[80px]"
                                data-testid="site-description-input"
                            />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                                className="h-4 w-4 accent-[#0066D9]"
                                data-testid="site-active-checkbox"
                            />
                            <span className="text-sm font-medium text-slate-700">Active</span>
                            <span className="text-xs text-slate-500">— shown in dropdowns</span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={form.requires_sublocation}
                                onChange={(e) => setForm((f) => ({ ...f, requires_sublocation: e.target.checked }))}
                                className="h-4 w-4 accent-[#0066D9] mt-0.5"
                                data-testid="site-requires-sublocation-checkbox"
                            />
                            <span>
                                <span className="text-sm font-medium text-slate-700">Requires sub-location / Data Hall</span>
                                <span className="block text-xs text-slate-500">
                                    Technicians must pick a Data Hall before they can complete Clock Out.
                                </span>
                            </span>
                        </label>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOpen(false)}
                            data-testid="site-cancel-button"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="site-save-button"
                        >
                            {saving ? "Saving…" : editing ? "Save changes" : "Create site"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SubLocationManager
                site={subSite}
                open={!!subSite}
                onOpenChange={(o) => !o && setSubSite(null)}
            />
        </div>
    );
}
